import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import cn from "classnames";
import { CalendarDaysIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import Dropdown from "../Settings/Dropdown";
import { api, downloadFile } from "./api";

// Mirrors TIMELINE_PARTS in server/src/services/investigations/exports.ts.
const PARTS: { key: string; label: string; hint: string }[] = [
  { key: "calls", label: "Calls", hint: "Each recorded call with the brief's narrative for it" },
  { key: "flags", label: "Flagged moments", hint: "Timestamped quotes under their call" },
  { key: "files", label: "Documents", hint: "Documents, spreadsheets, and photos at their event dates" },
  { key: "brief", label: "Brief entries", hint: "Dated events from the brief, plus the undated ones" },
];
const CHECK = "checkbox checkbox-sm [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500";

interface Props {
  open: boolean;
  onClose: () => void;
  investigationId: string;
  hiddenCount?: number;
}

// The Timeline tab as a document, with the tab's own filters. Hidden items
// stay out unless asked for (then they print marked), notes print under
// their entries.
const ExportTimelineModal: React.FC<Props> = ({ open, onClose, investigationId, hiddenCount = 0 }) => {
  const [parts, setParts] = useState<Set<string>>(new Set(PARTS.map((p) => p.key)));
  const [minSeverity, setMinSeverity] = useState(3);
  const [includeHidden, setIncludeHidden] = useState(false);
  const [includeNotes, setIncludeNotes] = useState(true);
  const [format, setFormat] = useState<"pdf" | "docx">("pdf");
  const [exporting, setExporting] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
    if (open) {
      setParts(new Set(PARTS.map((p) => p.key)));
      setMinSeverity(3);
      setIncludeHidden(false);
      setIncludeNotes(true);
    }
  }, [open]);

  const toggle = (key: string) =>
    setParts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const handleExport = async () => {
    if (!parts.size) return;
    setExporting(true);
    try {
      await downloadFile(
        api.timelineUrl(investigationId, { include: PARTS.filter((p) => parts.has(p.key)).map((p) => p.key), minSeverity, includeHidden, includeNotes, format }),
        `investigation-timeline.${format}`
      );
      toast.success("Timeline downloaded");
      onClose();
    } catch (e: any) {
      toast.error(e.message || "Failed to export the timeline");
    } finally {
      setExporting(false);
    }
  };

  return (
    <dialog ref={dialogRef} className="modal" onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <div className="modal-box max-w-lg p-0 overflow-hidden rounded-2xl">
        <div className="flex items-start gap-3 px-6 pt-6 pb-4">
          <div className="size-10 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center">
            <CalendarDaysIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-base-content leading-tight">Export timeline</h3>
            <p className="text-sm text-base-content/60 mt-0.5">The chronology as PDF or Word, day by day, exactly as the Timeline tab shows it.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
            <XMarkIcon className="size-5" />
          </button>
        </div>
        <div className="px-6 space-y-4">
          <div>
            <p className="text-xs font-semibold text-base-content/70 mb-1.5">Include</p>
            <div className="rounded-lg border border-base-300 divide-y divide-base-300 bg-base-100">
              {PARTS.map((p) => {
                const checked = parts.has(p.key);
                const disabled = p.key === "flags" && !parts.has("calls");
                return (
                  <label key={p.key} className={cn("flex items-center gap-3 px-3 py-2 hover:bg-base-200", disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer", checked && !disabled && "bg-blue-50/60 dark:bg-blue-900/20")}>
                    <input type="checkbox" className={CHECK} checked={checked && !disabled} disabled={disabled} onChange={() => toggle(p.key)} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-base-content">{p.label}</span>
                      <span className="block text-[11px] text-base-content/50">{disabled ? "Flags print under their call; turn Calls on to include them." : p.hint}</span>
                    </span>
                    {p.key === "flags" && checked && !disabled && (
                      <span onClick={(e) => e.preventDefault()}>
                        <Dropdown size="xs" dense options={[1, 2, 3, 4, 5].map((s) => ({ value: String(s), label: `sev ≥ ${s}` }))} value={String(minSeverity)} onChange={(v) => setMinSeverity(Number(v))} />
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
          <div className="rounded-lg border border-base-300 divide-y divide-base-300 bg-base-100">
            <label className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-base-200">
              <input type="checkbox" className={CHECK} checked={includeNotes} onChange={(e) => setIncludeNotes(e.target.checked)} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-base-content">Detective's notes</span>
                <span className="block text-[11px] text-base-content/50">Printed under their entries with the author's name</span>
              </span>
            </label>
            <label className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-base-200">
              <input type="checkbox" className={CHECK} checked={includeHidden} onChange={(e) => setIncludeHidden(e.target.checked)} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-base-content">Hidden items{hiddenCount > 0 ? ` (${hiddenCount})` : ""}</span>
                <span className="block text-[11px] text-base-content/50">Items you hid on the timeline. Off leaves them out; on prints them like any other entry.</span>
              </span>
            </label>
          </div>
        </div>
        <div className="px-6 py-5 flex items-center gap-2">
          <Dropdown size="sm" dense direction="top" menuAlign="start" contentClassName="!z-40" options={[{ value: "pdf", label: "PDF" }, { value: "docx", label: "Word (.docx)" }]} value={format} onChange={(v) => setFormat(v as "pdf" | "docx")} />
          <div className="ml-auto flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={exporting}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleExport} disabled={exporting || !parts.size}>
              {exporting ? "Generating…" : `Download ${format === "docx" ? "Word" : "PDF"}`}
            </Button>
          </div>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={onClose}>close</button>
      </form>
    </dialog>
  );
};

export default ExportTimelineModal;
